import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Filter as FilterIcon, X, ChevronDown } from 'lucide-react';
import './FilterBar.css';

/**
 * FilterBar — Universal single-line filter component for LIS list pages.
 *
 * Props:
 *  - options:       [{ key, value }]  — Searchable field options
 *  - statusOptions: [{ key, value }]  — Status toggle options (Active / In-Active)
 *  - additionalFilterControls: React elements for page-specific filter fields
 *  - additionalFilterBadges: [{ id, label, value, onRemove }] active filter chips
 *  - filters:       Current filter state object
 *  - updateFilter:         (name, value) => void
 *  - updateMultipleFilters:(obj)        => void
 *  - keywordKey:    Key name for selected field (default: "keyword")
 *  - textKey:       Key name for search text (default: "text")
 *  - statusKey:     Key name for status (default: "is_active")
 */
const FilterBar = ({
  options = [],
  statusOptions = [],
  applicationStatusOptions = [],
  additionalFilterControls = null,
  additionalFilterBadges = [],
  filters = {},
  appliedFilters,
  updateFilter,
  updateMultipleFilters,
  keywordKey = 'keyword',
  textKey = 'text',
  fieldSelectTextKey = textKey,
  preserveSearchOnFieldSelect = false,
  statusKey = 'is_active',
  applicationStatusKey = 'application_status',
  searchValue,
  onSearchValueChange,
  onFieldSelect,
  onApplySearch,
  selectedOptionKey,
  appliedSelectedOptionKey,
  hiddenFilterKeys = [],
  onStatusSelect,
  onStatusRemove,
  onFilterRemove,
}) => {
  const filtersForBadges = appliedFilters || filters;
  const [inputValue, setInputValue] = useState(filters[textKey] || '');
  const [isFieldDropdownOpen, setIsFieldDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isAppStatusDropdownOpen, setIsAppStatusDropdownOpen] = useState(false);
  const [fieldSearchTerm, setFieldSearchTerm] = useState('');

  const inputRef = useRef(null);
  const fieldDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const appStatusDropdownRef = useRef(null);

  /* Current selected search field */
  const selectedOption = useMemo(() => {
    if (selectedOptionKey !== undefined) {
      return options.find(
        (option) => String(option.key) === String(selectedOptionKey),
      );
    }
    return options.find(o => String(o.key) === String(filters[keywordKey])) || options[0];
  }, [options, filters, keywordKey, selectedOptionKey]);

  const selectedOptionForBadges = useMemo(() => {
    if (appliedSelectedOptionKey !== undefined) {
      return options.find(
        (option) => String(option.key) === String(appliedSelectedOptionKey),
      );
    }
    return selectedOption;
  }, [options, appliedSelectedOptionKey, selectedOption]);

  /* Dynamic placeholder based on selected field */
  const currentPlaceholder = useMemo(() => {
    if (selectedOption?.value) {
      return `Search with ${selectedOption.value}...`;
    }
    return 'Search filter field...';
  }, [selectedOption]);

  /* Filter dropdown options by field search if typed */
  const visibleOptions = useMemo(() => {
    if (!fieldSearchTerm) return options;
    return options.filter(o =>
      o.value.toLowerCase().includes(fieldSearchTerm.toLowerCase())
    );
  }, [options, fieldSearchTerm]);

  /* Current status option */
  const currentStatus = useMemo(() => {
    if (filters[statusKey] === undefined || filters[statusKey] === null || filters[statusKey] === '') {
      return null;
    }
    return statusOptions.find(o => String(o.key) === String(filters[statusKey])) || null;
  }, [statusOptions, filters, statusKey]);

  const appliedStatus = useMemo(() => {
    const value = filtersForBadges[statusKey];
    if (value === undefined || value === null || value === "") return null;
    return statusOptions.find((option) => String(option.key) === String(value)) || null;
  }, [statusOptions, filtersForBadges, statusKey]);

  /* Applied search badge */
  const appliedSearchText = filtersForBadges[textKey];

  const appliedAppStatus = useMemo(() => {
    const val = filtersForBadges[applicationStatusKey];
    if (!val) return null;
    return applicationStatusOptions.find(
      (option) => String(option.key) === String(val),
    ) || null;
  }, [applicationStatusOptions, filtersForBadges, applicationStatusKey]);

  const formatBadgeValue = (value) => {
    if (Array.isArray(value)) return value.map(formatBadgeValue).join(", ");
    if (value && typeof value === "object") {
      if (value.start || value.end) {
        return [value.start, value.end].filter(Boolean).join(" – ");
      }
      return Object.values(value).map(formatBadgeValue).filter(Boolean).join(", ");
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value ?? "");
  };

  const getFilterLabel = (name) => {
    const option = options.find((item) => String(item.key) === String(name));
    if (option?.value) return option.value;
    return String(name)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const reservedFilterKeys = new Set([
    keywordKey,
    textKey,
    statusKey,
    applicationStatusKey,
    ...hiddenFilterKeys,
  ]);
  const additionalBadgeKeys = new Set(
    additionalFilterBadges.map((badge) => badge?.id),
  );
  const genericFilterBadges = Object.entries(filtersForBadges)
    .filter(([key, value]) => {
      if (reservedFilterKeys.has(key) || additionalBadgeKeys.has(key)) return false;
      return value !== undefined && value !== null && value !== "" && value !== -1;
    })
    .map(([key, value]) => ({
      id: key,
      label: getFilterLabel(key),
      value: formatBadgeValue(value),
    }))
    .filter((badge) => badge.value);

  /* Sync internal input with filters.text changes */
  useEffect(() => {
    setInputValue(filters[textKey] || '');
  }, [filters[textKey]]);

  /* Close dropdowns when clicking outside */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fieldDropdownRef.current && !fieldDropdownRef.current.contains(e.target)) {
        setIsFieldDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setIsStatusDropdownOpen(false);
      }
      if (appStatusDropdownRef.current && !appStatusDropdownRef.current.contains(e.target)) {
        setIsAppStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* Handle option selection */
  const handleSelectField = (option) => {
    const searchText = searchValue === undefined ? inputValue : searchValue;
    const nextSearchText = preserveSearchOnFieldSelect ? searchText : '';
    if (onFieldSelect) {
      onFieldSelect(option);
    } else if (updateMultipleFilters) {
      updateMultipleFilters({
        [keywordKey]: option.key,
        [fieldSelectTextKey]: nextSearchText,
      });
    } else if (updateFilter) {
      updateFilter(keywordKey, option.key);
      updateFilter(fieldSelectTextKey, nextSearchText);
    }
    setInputValue(nextSearchText);
    setIsFieldDropdownOpen(false);
    setFieldSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  /* Apply search on button click or Enter key */
  const handleApply = useCallback(() => {
    const trimmed = (
      searchValue === undefined ? inputValue : searchValue
    ).trim();
    if (onApplySearch) {
      onApplySearch(trimmed, selectedOption);
    } else if (updateFilter) {
      updateFilter(textKey, trimmed);
    }
  }, [
    inputValue,
    onApplySearch,
    searchValue,
    selectedOption,
    textKey,
    updateFilter,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  /* Clear search input */
  const handleClearInput = () => {
    setInputValue('');
    if (onSearchValueChange) onSearchValueChange('', selectedOption);
    if (onApplySearch) {
      onApplySearch('', selectedOption);
    } else if (filters[textKey] && updateFilter) {
      updateFilter(textKey, '');
    }
    inputRef.current?.focus();
  };

  /* Remove search filter badge */
  const handleRemoveSearchBadge = () => {
    setInputValue('');
    if (onFilterRemove) {
      onFilterRemove(textKey);
    } else if (updateFilter) {
      updateFilter(textKey, '');
    }
  };

  /* Remove status filter badge */
  const handleRemoveStatusBadge = () => {
    if (onStatusRemove) {
      onStatusRemove();
    } else if (updateFilter) {
      // Clear status filter or reset to empty
      updateFilter(statusKey, '');
    }
  };

  /* Select status */
  const handleSelectStatus = (optionKey) => {
    if (onStatusSelect) {
      onStatusSelect(optionKey);
    } else if (updateFilter) {
      updateFilter(statusKey, optionKey);
    }
    setIsStatusDropdownOpen(false);
  };

  /* Current application status option */
  const currentAppStatus = useMemo(() => {
    const val = filters[applicationStatusKey];
    if (!val) return null;
    return applicationStatusOptions.find(o => String(o.key) === String(val)) || null;
  }, [applicationStatusOptions, filters, applicationStatusKey]);

  /* Select application status */
  const handleSelectAppStatus = (optionKey) => {
    if (updateFilter) {
      updateFilter(applicationStatusKey, optionKey);
    }
    setIsAppStatusDropdownOpen(false);
  };

  /* Remove application status badge */
  const handleRemoveAppStatusBadge = () => {
    if (onFilterRemove) {
      onFilterRemove(applicationStatusKey);
    } else if (updateFilter) {
      updateFilter(applicationStatusKey, '');
    }
  };

  return (
    <div className="lisFilterContainer">
      {/* ── Single line filter bar ── */}
      <div className="lisFilterBar">
        {/* Filter label + funnel icon */}
        <div className="lisFilterLabel">
          <FilterIcon size={15} strokeWidth={2.4} className="lisFilterFunnelIcon" />
          <span>Filter</span>
        </div>

        {/* Search input with dropdown trigger */}
        <div className="lisFilterInputWrapper" ref={fieldDropdownRef}>
          <div className="lisFilterInputInner">
            <input
              ref={inputRef}
              type="text"
              className="lisFilterInput"
              placeholder={currentPlaceholder}
              value={searchValue === undefined ? inputValue : searchValue}
              onChange={(e) => {
                const value = e.target.value;
                if (onSearchValueChange) onSearchValueChange(value, selectedOption);
                else setInputValue(value);
              }}
              onKeyDown={handleKeyDown}
            />

            {/* Clear 'X' inside input when text is typed */}
              {(searchValue === undefined ? inputValue : searchValue) && (
              <button
                type="button"
                className="lisFilterInputClearBtn"
                onClick={handleClearInput}
                title="Clear input"
              >
                <X size={13} strokeWidth={2.5} />
              </button>
            )}

            {/* Field selector dropdown arrow */}
            {options.length > 0 && (
              <button
                type="button"
                className={`lisFilterDropdownToggle ${isFieldDropdownOpen ? 'open' : ''}`}
                onClick={() => setIsFieldDropdownOpen((prev) => !prev)}
                title="Select field to filter"
              >
                <ChevronDown size={14} strokeWidth={2.2} />
              </button>
            )}
          </div>

          {/* Dropdown menu showing options */}
          {isFieldDropdownOpen && options.length > 0 && (
            <div className="lisFilterDropdownMenu">
              <div className="lisFilterDropdownHeader">
                <input
                  type="text"
                  className="lisFilterDropdownSearch"
                  placeholder="Search filter field..."
                  value={fieldSearchTerm}
                  onChange={(e) => setFieldSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
              <ul className="lisFilterDropdownList">
                {visibleOptions.map((opt) => {
                  const isSelected = String(opt.key) === String(selectedOption?.key);
                  return (
                    <li
                      key={String(opt.key)}
                      className={`lisFilterDropdownItem ${isSelected ? 'active' : ''}`}
                      onClick={() => handleSelectField(opt)}
                    >
                      <span>{opt.value}</span>
                      {isSelected && <span className="lisFilterCheck">✓</span>}
                    </li>
                  );
                })}
                {visibleOptions.length === 0 && (
                  <li className="lisFilterDropdownEmpty">No matching fields</li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Apply button */}
        <button
          type="button"
          className="lisFilterApplyBtn"
          onClick={handleApply}
        >
          Apply
        </button>

        {/* Status Dropdown (Active / In-Active) */}
        {statusOptions.length > 0 && (
          <div className="lisFilterStatusWrapper" ref={statusDropdownRef}>
            <button
              type="button"
              className="lisFilterStatusBtn"
              onClick={() => setIsStatusDropdownOpen((prev) => !prev)}
            >
              <span>{currentStatus ? currentStatus.value : 'Status'}</span>
              <ChevronDown size={13} strokeWidth={2.2} className={isStatusDropdownOpen ? 'rotate' : ''} />
            </button>

            {isStatusDropdownOpen && (
              <ul className="lisFilterStatusMenu">
                {statusOptions.map((opt) => (
                  <li
                    key={String(opt.key)}
                    className={`lisFilterStatusItem ${String(filters[statusKey]) === String(opt.key) ? 'active' : ''}`}
                    onClick={() => handleSelectStatus(opt.key)}
                  >
                    {opt.value}
                  </li>
                ))}
                <li
                  className={`lisFilterStatusItem ${filters[statusKey] === '' || filters[statusKey] === null || filters[statusKey] === undefined ? 'active' : ''}`}
                  onClick={() => handleSelectStatus('')}
                >
                  All
                </li>
              </ul>
            )}
          </div>
        )}

        {/* Application Status Dropdown (Approved / Rejected / Pending) */}
        {applicationStatusOptions.length > 0 && (
          <div className="lisFilterStatusWrapper" ref={appStatusDropdownRef}>
            <button
              type="button"
              className="lisFilterStatusBtn"
              onClick={() => setIsAppStatusDropdownOpen((prev) => !prev)}
            >
              <span>{currentAppStatus ? currentAppStatus.value : 'App. Status'}</span>
              <ChevronDown size={13} strokeWidth={2.2} className={isAppStatusDropdownOpen ? 'rotate' : ''} />
            </button>

            {isAppStatusDropdownOpen && (
              <ul className="lisFilterStatusMenu">
                {applicationStatusOptions.map((opt) => (
                  <li
                    key={String(opt.key)}
                    className={`lisFilterStatusItem ${String(filters[applicationStatusKey]) === String(opt.key) ? 'active' : ''}`}
                    onClick={() => handleSelectAppStatus(opt.key)}
                  >
                    {opt.value}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ── Badges Row ── */}
      {additionalFilterControls && (
        <div className="lisFilterAdditionalControls">
          {additionalFilterControls}
        </div>
      )}

      <div className="lisFilterBadges">
        {/* Status Badge */}
        {appliedStatus && (
          <span className="lisFilterBadge">
            <span>Status: {appliedStatus.value}</span>
            <button
              type="button"
              className="lisFilterBadgeRemove"
              onClick={handleRemoveStatusBadge}
              title="Remove status filter"
            >
              <X size={11} strokeWidth={2.6} />
            </button>
          </span>
        )}

        {/* Application Status Badge */}
        {appliedAppStatus && (
          <span className="lisFilterBadge">
            <span>Application: {appliedAppStatus.value}</span>
            <button
              type="button"
              className="lisFilterBadgeRemove"
              onClick={handleRemoveAppStatusBadge}
              title="Remove application status filter"
            >
              <X size={11} strokeWidth={2.6} />
            </button>
          </span>
        )}

        {/* Search Field Badge */}
        {appliedSearchText && (
          <span className="lisFilterBadge">
            <span>{selectedOptionForBadges?.value || 'Search'}: {appliedSearchText}</span>
            <button
              type="button"
              className="lisFilterBadgeRemove"
              onClick={handleRemoveSearchBadge}
              title="Remove search filter"
            >
              <X size={11} strokeWidth={2.6} />
            </button>
          </span>
        )}

        {genericFilterBadges.map((badge) => (
          <span className="lisFilterBadge" key={badge.id}>
            <span>{badge.label}: {badge.value}</span>
            <button
              type="button"
              className="lisFilterBadgeRemove"
              onClick={() =>
                onFilterRemove
                  ? onFilterRemove(badge.id)
                  : updateFilter?.(badge.id, "")
              }
              title={`Remove ${badge.label} filter`}
              aria-label={`Remove ${badge.label} filter`}
            >
              <X size={11} strokeWidth={2.6} />
            </button>
          </span>
        ))}

        {additionalFilterBadges
          .filter((badge) => badge?.value !== undefined && badge?.value !== null && badge?.value !== "")
          .map((badge) => (
            <span className="lisFilterBadge" key={badge.id}>
              <span>{badge.label}: {badge.value}</span>
              <button
                type="button"
                className="lisFilterBadgeRemove"
                onClick={badge.onRemove}
                title={`Remove ${badge.label} filter`}
                aria-label={`Remove ${badge.label} filter`}
              >
                <X size={11} strokeWidth={2.6} />
              </button>
            </span>
          ))}
      </div>
    </div>
  );
};

export default FilterBar;
